import { useEffect, useState, useCallback, createContext, useContext, useRef } from "react";

const icons = {
  success: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 17l6-6 4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 7h6v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 11v5M12 8v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3l9 16H3l9-16z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M12 10v4M12 17v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
};

/* Variant styling — teal is the dashboard's positive accent, the rest
   follow the same "tinted icon chip + quiet white card" pattern.       */
const variants = {
  success: {
    icon: "bg-teal-50 text-teal-600",
    bar: "bg-teal-500",
  },
  error: {
    icon: "bg-rose-50 text-rose-600",
    bar: "bg-rose-500",
  },
  info: {
    icon: "bg-slate-100 text-slate-500",
    bar: "bg-slate-400",
  },
  warning: {
    icon: "bg-amber-50 text-amber-600",
    bar: "bg-amber-500",
  },
};

/* ------------------------------------------------------------------ */
/* Single toast                                                        */
/* ------------------------------------------------------------------ */
function Toast({ id, title, description, variant = "info", duration = 4000, onDismiss }) {
  const [leaving, setLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const startRef = useRef(Date.now());
  const remainingRef = useRef(duration);

  const style = variants[variant] ?? variants.info;

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onDismiss(id), 200);
  }, [id, onDismiss]);

  const clearTimers = () => {
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
  };

  const start = useCallback(() => {
    startRef.current = Date.now();
    timeoutRef.current = setTimeout(dismiss, remainingRef.current);
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
    }, 30);
  }, [dismiss, duration]);

  const pause = () => {
    clearTimers();
    remainingRef.current -= Date.now() - startRef.current;
  };

  useEffect(() => {
    start();
    return clearTimers;
   
  }, []);

  return (
    <div
      role="status"
      onMouseEnter={pause}
      onMouseLeave={start}
      className={`relative w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg
        transition-all duration-200 ease-out
        ${leaving ? "opacity-0 translate-x-2" : "opacity-100 translate-x-0 animate-toast-in"}`}
    >
      <div className="flex items-start gap-3 p-4 pr-9">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${style.icon}`}>
          {icons[variant]}
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          {title && <p className="text-sm font-semibold text-gray-800">{title}</p>}
          {description && (
            <p className={`text-xs text-gray-500 ${title ? "mt-0.5" : ""}`}>{description}</p>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss notification"
          className="absolute right-3 top-3 text-gray-300 transition-colors hover:text-gray-500"
        >
          {icons.close}
        </button>
      </div>

    
      <div className="h-0.5 w-full bg-gray-100">
        <div
          className={`h-full ${style.bar} transition-[width] duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}


const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((opts) => {
    const id = opts.id ?? crypto.randomUUID();
    setToasts((prev) => [...prev, { id, variant: "info", duration: 4000, ...opts }]);
    return id;
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <Toast {...t} onDismiss={dismiss} />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(8px) translateX(8px); }
          to { opacity: 1; transform: translateY(0) translateX(0); }
        }
        .animate-toast-in { animation: toast-in 0.25s ease-out; }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}

/* ------------------------------------------------------------------ */
/* Demo                                                                 */
/* ------------------------------------------------------------------ */
